import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Loader2 } from "lucide-react";
import PasswordInput from "../components/PasswordInput";

function safeNext(raw) {
  if (!raw) return null;
  try {
    const decoded = decodeURIComponent(raw);
    if (decoded.startsWith("/") && !decoded.startsWith("//")) return decoded;
  } catch { /* ignore */ }
  return null;
}

export default function Signup() {
  const { signUp, signIn } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const nextPath = safeNext(searchParams.get("next"));
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    const { data, error } = await signUp({ email, password, name });
    if (error) {
      setError(error.message);
      setBusy(false);
      return;
    }
    // If email confirmation is disabled, a session is created instantly.
    if (!data?.session) {
      // Some Supabase projects still return no session — try password sign-in.
      const { error: e2 } = await signIn({ email, password });
      if (e2) {
        setError("Account created. Please disable email confirmation in Supabase to enable instant signup.");
        setBusy(false);
        return;
      }
    }
    // Preserve the deep-link through onboarding so the user still lands on
    // the shared content after they finish setup.
    navigate(
      nextPath ? `/onboarding?next=${encodeURIComponent(nextPath)}` : "/onboarding",
      { replace: true },
    );
  };

  return (
    <AuthShell title="Begin your glow" subtitle="Create your free account in seconds.">
      <form onSubmit={submit} className="space-y-3" data-testid="signup-form">
        <input
          type="text"
          required
          placeholder="First name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          data-testid="signup-name"
          className="w-full rounded-full bg-white border border-cocoa/10 px-5 py-3.5 text-sm placeholder:text-cocoa/40 focus:outline-none focus:ring-2 focus:ring-rose/40"
        />
        <input
          type="email"
          required
          placeholder="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          data-testid="signup-email"
          className="w-full rounded-full bg-white border border-cocoa/10 px-5 py-3.5 text-sm placeholder:text-cocoa/40 focus:outline-none focus:ring-2 focus:ring-rose/40"
        />
        <PasswordInput
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Password (min 6 characters)"
          autoComplete="new-password"
          minLength={6}
          testId="signup-password"
          className="rounded-full bg-white border border-cocoa/10 px-5 py-3.5 text-sm placeholder:text-cocoa/40 focus:outline-none focus:ring-2 focus:ring-rose/40"
        />
        {error && <p className="text-sm text-red-500 font-light px-2" data-testid="signup-error">{error}</p>}
        <button
          type="submit"
          disabled={busy}
          data-testid="signup-submit"
          className="w-full rounded-full bg-rose hover:bg-rose-dark text-white py-3.5 font-medium transition-all shadow-rose flex items-center justify-center gap-2"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create account"}
        </button>
      </form>
      <p className="text-center text-sm text-cocoa/65 font-light mt-6">
        Already glowing?{" "}
        <Link to={nextPath ? `/login?next=${encodeURIComponent(nextPath)}` : "/login"} data-testid="signup-to-login" className="text-rose font-medium">
          Log in
        </Link>
      </p>
    </AuthShell>
  );
}

export function AuthShell({ title, subtitle, children }) {
  return (
    <div className="min-h-screen bg-cream flex flex-col">
      <Link to="/" data-testid="auth-back-home" className="flex items-center gap-2.5 px-6 py-5">
        <img src="/brand/logo.png" alt="Glow & Grow" className="w-9 h-9 rounded-full object-cover" />
        <span className="text-xl font-serif text-cocoa">Glow &amp; Grow</span>
      </Link>
      <div className="flex-1 px-6 pb-12 flex items-center justify-center">
        <div className="w-full max-w-md">
          <h1 className="font-serif text-4xl text-cocoa mb-1">{title}</h1>
          <p className="text-cocoa/60 font-light mb-7">{subtitle}</p>
          {children}
        </div>
      </div>
    </div>
  );
}
